/**
 * Defensive wrapper around a `Record<Status, { label, palette }>` map.
 * Returns a `{label: '—', palette: 'gray'}` fallback for unknown / null /
 * undefined status values so templates never crash on `meta[status].palette`.
 *
 * Use this for any status meta map consumed by template badges :
 *
 *   export const FOO_STATUS_META = defensiveStatusMeta({
 *     ACTIVE: { label: 'Actif', palette: 'brand' },
 *     ...
 *   });
 *
 * Template can keep its existing `meta[v.status].palette` syntax — the Proxy
 * intercepts unknown keys and returns the fallback.
 */
export type StatusPalette =
  | 'brand'
  | 'blue-light'
  | 'warning'
  | 'gray'
  | 'error';

export interface StatusMeta {
  label: string;
  palette: StatusPalette;
}

export const FALLBACK_STATUS_META: StatusMeta = {
  label: '—',
  palette: 'gray',
};

export function defensiveStatusMeta<T extends string>(
  map: Record<T, StatusMeta>,
): Record<T, StatusMeta> {
  return new Proxy(map, {
    get(target, prop) {
      const v = (target as Record<string, StatusMeta>)[prop as string];
      return v ?? FALLBACK_STATUS_META;
    },
  }) as Record<T, StatusMeta>;
}
